import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { toast } from "react-toastify";
import { fetchPolicyById } from "../policies/policySlice";
import UploadPolicyModal from "../renewals/UploadPolicy";
import RenewPolicy from "../renewals/RenewPolicy";
import SuccessDialog from "../addPolicy/SuccessDialog";
import { getVehicleTypeDisplayName } from "../../utils/vehicleTypeMapper";
import MobilePaymentEditDialog from './MobilePaymentEditDialog';
import CircularProgress from '@mui/material/CircularProgress';

// MUI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BusinessIcon from "@mui/icons-material/Business";
import TagIcon from "@mui/icons-material/LocalOffer";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FlagIcon from "@mui/icons-material/Flag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import HandymanIcon from "@mui/icons-material/Handyman";
import NumbersIcon from "@mui/icons-material/Numbers";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ShareIcon from "@mui/icons-material/Share";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fab from '@mui/material/Fab';
import StatusBadge from "../../components/common/StatusBadge";
import { calculatePolicyStatus } from "../../utils/dateUtils";
import { useResponsiveFonts, truncateName, truncateText, truncateVehicleModel, truncateVehicleNumber } from "../../utils/responsiveFonts";

import { createWhatsAppMessage } from "../../utils/urlShortener";

// Icons for bottom navigation
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';

// Create aliases for bottom navigation
const PolicyIcon = DescriptionIcon;
const BalanceIcon = AccountBalanceWalletIcon;

const MobilePolicyDetail = () => {
  const dispatch = useDispatch();
  const { id: policyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentPolicy } = useSelector((state) => state.policies);
  const fonts = useResponsiveFonts() || {
    header: { xs: '12px', sm: '14px', md: '16px' },
    title: { xs: '10px', sm: '12px', md: '14px' },
    body: { xs: '8px', sm: '9px', md: '10px' },
    caption: { xs: '4px', sm: '5px', md: '6px' },
    button: { xs: '6px', sm: '7px', md: '8px' }
  };
  const contentRef = useRef(null);

  // Get the page and category we came from, default to 1 and 'motor' if not provided
  const fromPage = location.state?.fromPage || 1;
  const fromScreen = location.state?.fromScreen;
  const fromCategory = location.state?.fromCategory || 'motor';
  console.log('MobilePolicyDetail: fromPage received:', fromPage, 'fromScreen:', fromScreen, 'fromCategory:', fromCategory, 'location.state:', location.state);

  const [policy, setPolicy] = useState(location.state?.policy || null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRenewForm, setShowRenewForm] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [renewMode, setRenewMode] = useState("edit");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successPdfUrl, setSuccessPdfUrl] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const actionMenuOpen = Boolean(actionMenuAnchor);
  const [pdfAction, setPdfAction] = useState(null); // 'viewing' or 'sharing'

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      // For admin users viewing staff policies, use the policy's agentId
      // Otherwise fall back to current user's UID
      const userId = location.state?.policy?.agentId || user?.uid || location.state?.userId;
      if (!userId || !policyId) {
        console.log("Waiting for user/policyId...", { policyId, user: user?.uid });
        return;
      }

      // Use Redux action for optimized fetching with caching
      const result = await dispatch(fetchPolicyById({ agentId: userId, id: policyId }));
      const fetchedPolicy = result.payload;

      // Set policy from result directly to avoid stale closure issues
      if (fetchedPolicy) {
        setPolicy(fetchedPolicy);
      } else {
        // Fallback to direct Firebase query if not in Redux (shouldn't happen with proper caching)
        const policyRef = doc(db, `agents/${userId}/policies/${policyId}`);
        const snap = await getDoc(policyRef);

        if (snap.exists()) {
          const data = snap.data();
          console.log("📄 Policy fetched from Firestore:", data);
          setPolicy({ id: snap.id, ...data });
        } else {
          console.warn("❌ Policy not found at path.");
        }
      }
    } catch (err) {
      console.error("🔥 Error fetching policy:", err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Sync with currentPolicy from Redux when it updates
  useEffect(() => {
    if (currentPolicy && currentPolicy.id === policyId) {
      setPolicy(currentPolicy);
    }
  }, [currentPolicy, policyId]);

  // Reset/Initialize policy when route changes
  useEffect(() => {
    if (location.state?.policy) {
      setPolicy(location.state.policy);
    } else {
      setPolicy(null);
    }
  }, [policyId, location.state]);

  useEffect(() => {
    // Log debugging info (temporary)
    // console.log("MobilePolicyDetail mounted. ID:", policyId, "User:", user?.uid);

    // Determine effective user ID from available sources
    const effectiveUserId = location.state?.policy?.agentId || user?.uid || location.state?.userId;

    if (policyId && effectiveUserId) {
      fetchPolicy();
    } else {
      console.log("Waiting for user/policyId...", { policyId, user: user?.uid });
    }
  }, [policyId, user?.uid, location.state]);

  // Handle successful policy update
  const handlePolicyUpdateSuccess = async (pdfUrl) => {
    console.log("🎉 Policy update success called with mode:", renewMode);
    setIsRefreshing(true);
    setShowRenewForm(false);
    setPrefillData(null);

    // Show success dialog only for renewals, banner for edits
    if (renewMode === "update" && pdfUrl) {
      // Renewal - show SuccessDialog
      setSuccessPdfUrl(pdfUrl);
      setShowSuccessDialog(true);
    } else {
      // Edit - show toast banner
      toast.success("Policy successfully edited", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    }

    // Refresh the policy data
    await fetchPolicy();
  };

  // Handle edit policy
  const handleEditPolicy = () => {
    setEditRenewDialogOpen(false);
    setRenewMode("edit");
    setPrefillData(null); // Clear prefill data for edit mode
    setShowRenewForm(true);
  };

  // Handle renew policy
  const handleRenewPolicy = () => {
    setEditRenewDialogOpen(false);
    setRenewMode("update"); // Changed from "renew" to "update" to use existing policy ID
    setShowUploadModal(true);
  };

  // Handle phone call
  const handlePhoneCall = () => {
    if (policy?.mobile) {
      window.location.href = `tel:${policy.mobile}`;
    }
  };

  // Handle share PDF
  const handleSharePdf = async () => {
    if (!policy?.pdfUrl && !policy?.driveFileId) {
      console.error("No PDF URL or Drive file available for sharing");
      return;
    }

    if (pdfAction) return;
    setPdfAction('sharing');

    // Detect if we're on mobile
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    try {
      let shareUrl = policy.pdfUrl;

      // For Drive-stored policies, create a shareable link
      if (policy.pdfSource === "drive" && policy.driveFileId) {
        try {
          const { createDriveShareLink } = await import("../../utils/driveApi");
          const shareLinks = await createDriveShareLink(policy.driveFileId);
          shareUrl = shareLinks.shareableLink;
          console.log("✅ Drive share link created:", shareUrl);
        } catch (driveError) {
          console.error("❌ Failed to create Drive share link:", driveError);
          if (policy.pdfUrl) {
            console.log("🔗 Using fallback pdfUrl for sharing");
            shareUrl = policy.pdfUrl;
          } else {
            const isNetworkError = driveError.message?.includes('Failed to fetch') || driveError.message?.includes('NetworkError');
            toast.error(isNetworkError ? "Connection error: Could not reach backend server." : "Unable to create share link. Document may have been deleted.");
            return;
          }
        }
      }

      let shareText;

      // Use PolicyTrackr's own short link system (no CORS issues with Firestore)
      // Works on both mobile and desktop
      try {
        shareText = await createWhatsAppMessage(shareUrl, user?.displayName, policy.id, true);
        console.log('✅ Short URL created for sharing');
      } catch (shortUrlError) {
        console.error('Failed to create short URL, using original:', shortUrlError);
        shareText = `You can view and download your policy here: ${shareUrl}`;
      }

      const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(shareText)}`;
      const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;

      if (isMobileDevice) {
        // On mobile, use direct navigation for better app opening
        window.location.href = whatsappAppUrl;
      } else {
        // Desktop approach with iframe and fallback
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = whatsappAppUrl;
        document.body.appendChild(iframe);

        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 100);

        let appOpened = false;
        const handleBlur = () => {
          appOpened = true;
          window.removeEventListener('blur', handleBlur);
        };
        window.addEventListener('blur', handleBlur);

        setTimeout(() => {
          window.removeEventListener('blur', handleBlur);
          if (!appOpened) {
            window.open(webWhatsappUrl, "_blank", "noopener,noreferrer");
          }
        }, 1500);
      }
    } catch (error) {
      console.error("Error sharing PDF:", error);
      const isNetworkError = error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError');
      toast.error(isNetworkError ? "Connection error: Could not reach backend server." : "Failed to share policy");
      // Fallback - try with original URL if available
      if (policy.pdfUrl) {
        const fallbackText = `Your policy document: ${policy.pdfUrl}`;
        const fallbackUrl = `whatsapp://send?text=${encodeURIComponent(fallbackText)}`;
        window.location.href = fallbackUrl;
      }
    } finally {
      setPdfAction(null);
      setActionMenuAnchor(null);
    }
  };

  // Handle view PDF
  const handleViewPdf = async () => {
    if (!policy?.pdfUrl && !policy?.driveFileId) {
      console.error("No PDF URL or Drive file available for viewing");
      return;
    }

    if (pdfAction) return;
    setPdfAction('viewing');

    // On mobile, window.open MUST be called synchronously within the user gesture
    // (click handler). If called after an await, mobile browsers block it as a popup.
    // So we open a blank window immediately, then navigate it after the async call.
    let newWindow = null;

    try {
      // For Drive-stored policies, get view URL from backend
      if (policy.pdfSource === "drive" && policy.driveFileId) {
        // Open blank window immediately to preserve user gesture on mobile
        newWindow = window.open('about:blank', '_blank');

        try {
          const { getDriveViewUrl } = await import("../../utils/driveApi");
          const viewUrl = await getDriveViewUrl(policy.driveFileId);

          if (newWindow && !newWindow.closed) {
            newWindow.location.href = viewUrl;
          } else {
            // Fallback if window was blocked despite our effort
            window.location.href = viewUrl;
          }
          setShowViewDialog(false);
          return;
        } catch (driveError) {
          console.error("❌ Failed to get Drive view URL:", driveError);
          if (policy.pdfUrl) {
            console.log("🔗 Using fallback pdfUrl for viewing");
            if (newWindow && !newWindow.closed) {
              newWindow.location.href = policy.pdfUrl;
            } else {
              window.location.href = policy.pdfUrl;
            }
            setShowViewDialog(false);
            return;
          }
          // Close the blank window on error
          if (newWindow && !newWindow.closed) {
            newWindow.close();
          }
          const isNetworkError = driveError.message?.includes('Failed to fetch') || driveError.message?.includes('NetworkError');
          toast.error(isNetworkError ? "Connection error: Could not reach backend server." : "Document unavailable. It may have been deleted from Google Drive.");
          return;
        }
      }

      // Firebase-stored policies - use direct URL (synchronous, no popup issue)
      window.open(policy.pdfUrl, "_blank", "noopener,noreferrer");
      setShowViewDialog(false);
    } finally {
      setPdfAction(null);
      setActionMenuAnchor(null);
    }
  };
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography>Loading policy details...</Typography>
      </Box>
    );
  }
  if (!policy) {
    console.log("📭 No policy passed in location state:", location);
    return <p>No policy data provided.</p>;
  }

  // Compute simple status from expiry (fallback to provided status)
  const computeStatus = () => {
    return calculatePolicyStatus(policy.expiryDate, policy.status) || "-";
  };

  const statusText = computeStatus();

  return (
    <Box
      className="mobile-layout"
      sx={{
        width: '100vw',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        overscrollBehavior: 'none',
        pb: '0 !important'
      }}
    >

      {/* Client card */}
      <Box
        sx={{
          width: '100%',
          height: { xs: 70, sm: 60 },
          px: { xs: 2, sm: 1.5 },
          py: { xs: 1.5, sm: 1.5 },
          backgroundColor: '#0063F7',
          color: '#FFFFFF',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          mx: 0,
          borderRadius: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000
        }}
      >
        <Avatar sx={{ width: 40, height: 40, bgcolor: '#E0EBFF', color: '#0063F7', mr: { xs: 2, sm: 2 }, flexShrink: 0 }}>
          <PersonIcon sx={{ fontSize: 24 }} />
        </Avatar>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          mr: 2
        }}>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 500,
            fontSize: { xs: '14px', sm: '15px' },
            lineHeight: '1.2',
            letterSpacing: '0.01em',
            opacity: 1,
            textAlign: 'left',
            mb: '2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}>{policy.name || '-'}</Typography>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: { xs: '12px', sm: '13px' },
            lineHeight: '1.2',
            letterSpacing: '0.01em',
            color: '#FFFFFF',
            opacity: 0.9,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}>{policy.mobile || '-'}</Typography>
        </Box>

        {policy.referredBy && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
            maxWidth: '35%',
            mr: 0.5
          }}>
            <Typography sx={{
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: { xs: '12px', sm: '13px' },
              lineHeight: '1.2',
              letterSpacing: '0.01em',
              color: '#FFFFFF',
              opacity: 0.9,
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}>
              Ref: {policy.referredBy}
            </Typography>
          </Box>
        )}

        {/* Action button with dropdown */}
        <Box sx={{ flexShrink: 0 }}>
          <IconButton
            onClick={(event) => setActionMenuAnchor(event.currentTarget)}
            sx={{
              color: '#FFFFFF',
              p: 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Menu
            anchorEl={actionMenuAnchor}
            open={actionMenuOpen}
            onClose={() => setActionMenuAnchor(null)}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: 120
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSharePdf();
              }}
              disabled={(!policy?.pdfUrl && !policy?.driveFileId) || pdfAction === 'sharing'}
              sx={{
                fontSize: '14px',
                fontFamily: 'Roboto',
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&.Mui-disabled': {
                  color: '#9E9E9E'
                }
              }}
            >
              {pdfAction === 'sharing' ? (
                <CircularProgress size={16} sx={{ color: '#166434' }} />
              ) : (
                <ShareIcon sx={{ fontSize: 16, color: '#166434' }} />
              )}
              Share
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleViewPdf();
              }}
              disabled={(!policy?.pdfUrl && !policy?.driveFileId) || pdfAction === 'viewing'}
              sx={{
                fontSize: '14px',
                fontFamily: 'Roboto',
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&.Mui-disabled': {
                  color: '#9E9E9E'
                }
              }}
            >
              {pdfAction === 'viewing' ? (
                <CircularProgress size={16} sx={{ color: '#0063F7' }} />
              ) : (
                <PictureAsPdfIcon sx={{ fontSize: 16, color: '#0063F7' }} />
              )}
              View
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Main content container */}
      <Box
        ref={contentRef}
        className="scroll-container"
        sx={{
          flex: 1,
          px: 0,
          pt: 1,
          pb: '120px',
          mt: '70px',
          overflowY: 'scroll',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          backgroundColor: '#f5f5f5'
        }}
      >
        {/* Details sections */}
        <Box sx={{
          width: 'calc(100% - 32px) !important',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          px: 2,
          pt: 2,
          pb: 12,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: 0,
          overflow: 'visible',
          mx: 2,
          mt: 1,
          boxSizing: 'border-box',
          maxWidth: 'calc(100vw - 32px) !important',
          minWidth: 'auto !important',
          flex: 'none !important'
        }}>
          {/* Vehicle Details Section */}
          {policy?.policyCategory === 'motor' && (
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '18px !important',
                  fontWeight: '600 !important',
                  color: '#1F2937',
                  letterSpacing: '0.025em',
                  px: 0
                }}
              >
                Vehicle Details
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: { xs: 2, sm: 3 },
                mt: 2
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mb: 2
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DirectionsCarIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                    <Typography
                      sx={{
                        fontSize: { xs: '14px !important', sm: '16px !important' },
                        fontWeight: 500,
                        color: "#6B7280",
                        textTransform: 'none',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Vehicle Type
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '18px !important', sm: '19px !important' },
                      fontWeight: 600,
                      color: "#111827",
                      mb: 0.5,
                      lineHeight: 1.3
                    }}
                  >
                    {getVehicleTypeDisplayName(policy.vehicleType)}
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mb: 2
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <NumbersIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                    <Typography
                      sx={{
                        fontSize: { xs: '14px !important', sm: '16px !important' },
                        fontWeight: 500,
                        color: "#6B7280",
                        textTransform: 'none',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Vehicle Number
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '18px !important', sm: '19px !important' },
                      fontWeight: 600,
                      color: "#111827",
                      mb: 0.5,
                      lineHeight: 1.3
                    }}
                  >
                    {policy.vehicleNumber || '-'}
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mb: 2
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <HandymanIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                    <Typography
                      sx={{
                        fontSize: { xs: '14px !important', sm: '16px !important' },
                        fontWeight: 500,
                        color: "#6B7280",
                        textTransform: 'none',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Make & Model
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '18px !important', sm: '19px !important' },
                      fontWeight: 600,
                      color: "#111827",
                      mb: 0.5,
                      lineHeight: 1.3
                    }}
                  >
                    {policy.vehicleMake && policy.vehicleModel ?
                      `${policy.vehicleMake} ${policy.vehicleModel}`.trim() :
                      (policy.vehicleMake || policy.vehicleModel || '-')
                    }
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Policy Details Section */}
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '18px !important',
                fontWeight: '600 !important',
                color: '#1F2937',
                letterSpacing: '0.025em',
                px: 0
              }}
            >
              Policy Details
            </Typography>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2, sm: 3 },
              mt: 2
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Company
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  {policy.policyCompany || '-'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: { xs: 2, sm: 3 },
              mt: 1
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2,
                minWidth: 0,
                overflow: 'hidden'
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ListAltIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Policy No
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3,
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere'
                  }}
                >
                  {policy.policyNumber || '-'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PolicyIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Policy Type
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  {policy.policyType || '-'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Expiry Date
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#E11D48",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  {policy.expiryDate || '-'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                alignItems: 'flex-start',
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <FlagIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Status
                  </Typography>
                </Box>
                <Box sx={{ mb: 0.5 }}>
                  <StatusBadge status={statusText} size="small" />
                </Box>
              </Box>

              {policy?.policyCategory !== 'motor' && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mb: 2
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TagIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                    <Typography
                      sx={{
                        fontSize: { xs: '14px !important', sm: '16px !important' },
                        fontWeight: 500,
                        color: "#6B7280",
                        textTransform: 'none',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Sum Insured
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '18px !important', sm: '19px !important' },
                      fontWeight: 600,
                      color: "#111827",
                      mb: 0.5,
                      lineHeight: 1.3
                    }}
                  >
                    ₹ {policy.sumInsured ? parseFloat(policy.sumInsured).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Payment Details Section */}
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '18px !important',
                fontWeight: '600 !important',
                color: '#1F2937',
                letterSpacing: '0.025em',
                px: 0
              }}
            >
              Payment Details
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: { xs: 2, sm: 3 },
              mt: 2,
              mb: 2
            }}>
              {policy?.policyCategory === "motor" && (
                <>
                  {policy.policyType !== "Liability" && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '14px !important', sm: '16px !important' },
                            fontWeight: 500,
                            color: "#6B7280",
                            textTransform: 'none',
                            letterSpacing: '0.05em'
                          }}
                        >
                          OD Premium
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '18px !important', sm: '19px !important' },
                          fontWeight: 600,
                          color: "#111827",
                          mb: 0.5,
                          lineHeight: 1.3
                        }}
                      >
                        ₹ {policy.odPremium ? parseFloat(policy.odPremium).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                      </Typography>
                    </Box>
                  )}

                  {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '14px !important', sm: '16px !important' },
                            fontWeight: 500,
                            color: "#6B7280",
                            textTransform: 'none',
                            letterSpacing: '0.05em'
                          }}
                        >
                          TP Premium
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '18px !important', sm: '19px !important' },
                          fontWeight: 600,
                          color: "#111827",
                          mb: 0.5,
                          lineHeight: 1.3
                        }}
                      >
                        ₹ {policy.tpPremium ? parseFloat(policy.tpPremium).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Net Premium
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  ₹ {policy.netPremium ? parseFloat(policy.netPremium).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Total Premium
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  ₹ {policy.premiumAmount ? parseFloat(policy.premiumAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Customer Discount
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  ₹ {policy.discount ? parseFloat(policy.discount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Amount Payable
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  ₹ {policy.amountPayable ? parseFloat(policy.amountPayable).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) :
                    (policy.premiumAmount ? (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-')}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SouthWestIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Amount Received
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#059669",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  ₹ {policy.paidAmount ? parseFloat(policy.paidAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Balance Amount
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#DC2626",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  ₹ {Number(Math.max((Number(policy.amountPayable || (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0))) || 0) - (Number(policy.paidAmount) || 0), 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mb: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '14px !important', sm: '16px !important' },
                      fontWeight: 500,
                      color: "#6B7280",
                      textTransform: 'none',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Mode of Payment
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px !important', sm: '19px !important' },
                    fontWeight: 600,
                    color: "#111827",
                    mb: 0.5,
                    lineHeight: 1.3
                  }}
                >
                  {policy.modeOfPayment || '-'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              mb: 2
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <DescriptionIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                <Typography
                  sx={{
                    fontSize: { xs: '14px !important', sm: '16px !important' },
                    fontWeight: 500,
                    color: "#6B7280",
                    textTransform: 'none',
                    letterSpacing: '0.05em'
                  }}
                >
                  Remarks
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: '16px !important', sm: '17px !important' },
                  fontWeight: 400,
                  color: "#374151",
                  mb: 0.5,
                  lineHeight: 1.4
                }}
              >
                {policy.remarks || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>


      {/* Edit Payment Dialog */}
      <MobilePaymentEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        policy={policy}
        onUpdate={fetchPolicy}
        userId={policy?.agentId || user?.uid}
      />

      {/* Edit FAB */}
      <Fab
        color="primary"
        onClick={() => setEditDialogOpen(true)}
        sx={{
          position: 'fixed',
          bottom: '24px !important',
          right: 16,
          zIndex: 1001,
          backgroundColor: '#0063F7 !important',
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(0, 99, 247, 0.4)',
          '&:hover': {
            backgroundColor: '#0052CC !important',
            boxShadow: '0 6px 16px rgba(0, 99, 247, 0.5)',
          }
        }}
      >
        <EditIcon />
      </Fab>
    </Box >
  );
};

export default MobilePolicyDetail;
